import { AfterViewInit, Component, ContentChildren, Input, OnDestroy, OnInit, QueryList, ChangeDetectionStrategy, 
    ChangeDetectorRef, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { DxButtonComponent, DxSwitchComponent, DxCheckBoxComponent, DxDateBoxComponent, DxNumberBoxComponent, DxSelectBoxComponent, 
    DxSliderComponent, DxTextAreaComponent, DxTextBoxComponent, DxDataGridComponent } from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { SysEventsService } from '@core/framework/sysevents.service';
import { FieldDetailComponent } from '../field-detail/fielddetail.component';
import { FieldDetailComponentX } from '../field-detailx/fielddetailx.component';
import { FieldFormComponent } from '../field-form/fieldform.component';
import { FieldFormComponentX } from '../field-formx/fieldformx.component';
import { GridExtender } from '@base/grid/Grid.extender';
import { GridWrapperDirective } from '@base/grid/Grid.directive';
import { ScreenService } from '@core/framework/screen.service';

@Component({
    selector: 'm4u-section',
    templateUrl: './section.component.html',
    styleUrls: ['./section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.section-with-shadow]': 'UseShadow' }
})
export class SectionComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input() Icon: string;
    @Input() Label: string;
    @Input() HideLabelOnMobile: boolean;
    @Input() Name: string;
    @Input() IsCollapsable: boolean;
    @Input() set IsCollapsed (value: boolean) {
        this.IsContentHidden = value;
    }
    @Input() Type: 'default' | 'outlined' = 'default';
    @Input() Style: 'light' | 'dark' = 'light';
    @Input() UseShadow: boolean;
    @Input() IsHideIcon: boolean;
    @Input() IsHeaderLink: boolean;
    @Output() OnHeaderClick = new EventEmitter<any>();
    @Output() OnSectionToggle = new EventEmitter<boolean>();
    @Output() OnClick = new EventEmitter<any>();

    IsContentHidden: boolean;

    private _visible: boolean;
    get visible(): boolean { 
        return this._visible;
    }
    set visible(value: boolean) { 
        this._visible = value; this.cdr.detectChanges();
    }

    @ContentChildren(DxSelectBoxComponent, { descendants: true }) private ctlSelectBoxes: QueryList<DxSelectBoxComponent>;
    @ContentChildren(DxTextBoxComponent, { descendants: true }) private ctlTextBoxes: QueryList<DxTextBoxComponent>;
    @ContentChildren(DxTextAreaComponent, { descendants: true }) private ctlTextAreas: QueryList<DxTextAreaComponent>;
    @ContentChildren(DxNumberBoxComponent, { descendants: true }) private ctlNumberBoxes: QueryList<DxNumberBoxComponent>;
    @ContentChildren(DxDateBoxComponent, { descendants: true }) private ctlDateBoxes: QueryList<DxDateBoxComponent>;
    @ContentChildren(DxCheckBoxComponent, { descendants: true }) private ctlCheckBoxes: QueryList<DxCheckBoxComponent>;
    @ContentChildren(DxSliderComponent, { descendants: true }) private ctlSliders: QueryList<DxSliderComponent>;
    @ContentChildren(DxButtonComponent, { descendants: true }) private ctlButtons: QueryList<DxButtonComponent>;
    @ContentChildren(DxSwitchComponent, { descendants: true }) private ctlSwitches: QueryList<DxSwitchComponent>;

    @ContentChildren(DxDataGridComponent, { descendants: true }) private grids: QueryList<DxDataGridComponent>;
    //explicit grids that defined inside custom component
    @ContentChildren(GridWrapperDirective, { descendants: true }) private editableGrids: QueryList<GridWrapperDirective>;


    //Fields
    @ContentChildren(FieldFormComponent, { descendants: true }) private fields1: QueryList<FieldFormComponent>;
    @ContentChildren(FieldFormComponentX, { descendants: true }) private fields2: QueryList<FieldFormComponentX>;
    @ContentChildren(FieldDetailComponent, { descendants: true }) private fields3: QueryList<FieldDetailComponent>;
    @ContentChildren(FieldDetailComponentX, { descendants: true }) private fields4: QueryList<FieldDetailComponentX>;

    @ViewChild('section') Section: ElementRef;

    private subscriptionOnSetDefaults: Subscription;
    constructor(public screen: ScreenService, private sysEvents: SysEventsService, private cdr: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.visible = true;
        this.subscriptionOnSetDefaults = this.sysEvents.OnSetDefaults.subscribe(x => {
            this.SetDefaultValues();
        });
    }

    ngOnDestroy() {
        this.subscriptionOnSetDefaults && this.subscriptionOnSetDefaults.unsubscribe();
    }

    ngAfterViewInit() {
        this.SetCustomizations();
    }
   
    //Lock all controls on form
    public LockControls(bLock: boolean) {
        this.Controls().forEach(controls => controls.forEach(x => x.readOnly = bLock));
        if (bLock) this.Grids().forEach(x => {
            (x.grid.instance as any).option(
                {
                    'editing.allowAdding': false,
                    'editing.allowDeleting': false,
                    'editing.allowUpdating': false
                });
        });
    }

    //Accept changes - sync the grid with the bound entity
    public AcceptGridChanges(): Promise<boolean>[] {
        return this.Grids().map(x => x?.ValidateGrid());
    }


    //Apply customizations
    protected SetCustomizations() {
        this.Fields().forEach(fields => fields.forEach(x => x.SetFieldCustomizations()));
    }

    //Set default values for the fields
    public SetDefaultValues() {
        this.Fields().forEach(fields => fields.forEach(x => x.SetFieldDefaultValue()));
    }

    public ResetValidation() {

        this.Controls().forEach(controls => controls.forEach(x => {
            x.instance.option(
                {
                    isValid: true,
                    validationError: undefined,
                    validationErrors: undefined,
                    validationStatus: 'valid'
                });
        }));
    }

    protected Grids(): GridExtender[] {
        const arr: GridExtender[] = [];
        this.grids.forEach(grid => arr.push((grid as any).G));
        this.editableGrids.forEach(grid => arr.push((grid as any).G));
        return arr;
    }

    //Return all control types
    protected Controls(): QueryList<any>[] {
        const arr =
            [
                this.ctlSelectBoxes,
                this.ctlTextBoxes,
                this.ctlTextAreas,
                this.ctlNumberBoxes,
                this.ctlDateBoxes,
                this.ctlCheckBoxes,
                this.ctlSliders,
                this.ctlButtons,
                this.ctlSwitches
            ];
        return arr;
    }

    //Return all fields
    protected Fields(): QueryList<FieldFormComponent>[] {
        const arr =
            [
                this.fields1,
                this.fields2,
                this.fields3,
                this.fields4
            ];
        return arr;
    }

    ToggleSection() {
        if (!this.IsCollapsable) {
            return;
        }

        this.IsContentHidden = !this.IsContentHidden;
        this.OnSectionToggle.emit(!this.IsContentHidden);
        if (!this.IsContentHidden) {
            setTimeout(() => this.ctlTextAreas.forEach(ta => ta.instance.repaint()));
        }
    }

    GetSectionClass() {
        return `section-${this.Type} section-${this.Style}`;
    }

    HeaderClick(event) {
        this.IsHeaderLink ? this.OnHeaderClick.emit(event) : this.ToggleSection();
    }
}
